import { useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  onSnapshot,
  Query,
  QuerySnapshot,
  SnapshotOptions,
} from "firebase/firestore";
import deepequal from "fast-deep-equal";

import { useFirestore } from "../../contexts/firebase/FirebaseContext";

import { generateQuery, QueryOp } from "./utils";

/**
 * Creates and caches a collection group reference.
 *
 * @param collectionGroupName The name of the collection group.
 * @returns `ref`
 */
export const useCollectionGroupReference = <T>(
  collectionGroupName?: string | null,
  operations?: (QueryOp | undefined | null)[] | null
): Query<T> | undefined => {
  const store = useFirestore();
  const [ops, setOps] = useState(operations);

  // Update operations if they change
  useEffect(() => {
    if (!deepequal(operations, ops)) setOps(operations);
  }, [operations, ops, setOps]);

  const ref = useMemo(() => {
    if (collectionGroupName && store)
      return generateQuery(
        collectionGroup(store, collectionGroupName),
        ops ?? []
      ) as Query<T>;
    return undefined;
  }, [collectionGroupName, store, ops]);

  return ref;
};

/**
 * Retrieve and listen to documents in a collection group.
 * A listener is created only when a non-empty collection group.
 *
 * Returns the following in an array:
 *  - The query snapshot when retrieved successfully. `undefined` otherwise.
 *  - A boolean representing the loading state of the query.
 *  - Error if an error occurred. `undefined` otherwise.
 *
 * @param collectionGroupName The name of the collection group to query.
 * @param operations The operations to apply to the query.
 * @returns `[snapshot, loading, error]`
 */
export const useCollectionGroup = <T>(
  collectionGroupName?: string | null,
  operations?: (QueryOp | undefined | null)[] | null
): [QuerySnapshot<T> | undefined, boolean, Error | undefined] => {
  const ref = useCollectionGroupReference<T>(collectionGroupName, operations);

  const defaultLoading = Boolean(collectionGroupName);
  const [loading, setLoading] = useState(defaultLoading);
  const [error, setError] = useState<Error>();
  const [snapshot, setSnapshot] = useState<QuerySnapshot<T>>();

  // Listen for query updates
  useEffect(() => {
    // If there is no ref, reset data
    if (!ref) {
      setLoading(defaultLoading);
      setSnapshot(undefined);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (cancelled) return;
        setSnapshot(snapshot);
        setLoading(false);
        setError(undefined);
      },
      (error) => {
        if (cancelled) return;
        setLoading(false);
        setError(error);
      }
    );

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [ref, defaultLoading, setLoading, setSnapshot, setError]);

  return [snapshot, loading, error];
};

/**
 * Retrieve and listen to a collection group's data from Firestore.
 * A listener is created only when a valid collection group is given.
 *
 * Returns the following in an array:
 *  - The collection group data. `undefined` otherwise.
 *  - A boolean representing the loading state of the query snapshot.
 *  - Error if an error occurred. `undefined` otherwise.
 *
 * @param collectionGroup The name of the collection group.
 * @param operations The operations to apply to the query.
 * @param options Snapshot options.
 * @returns `[data, loading, error]`
 */
export const useCollectionGroupData = <T>(
  collectionGroup?: string | null,
  operations?: (QueryOp | undefined | null)[] | null,
  options?: SnapshotOptions
): [T[] | undefined, boolean, Error | undefined] => {
  const [snapshot, loading, error] = useCollectionGroup<T>(
    collectionGroup,
    operations
  );
  const [opts, setOpts] = useState(options);
  const [data, setData] = useState<T[]>();

  // Update options if they change
  useEffect(() => {
    if (!deepequal(options, opts)) setOpts(options);
  }, [options, opts, setOpts]);

  // If the snapshot changes, extract the data
  useEffect(() => {
    setData(snapshot?.docs.map((snap) => snap.data(opts)));
  }, [setData, snapshot, opts]);

  return [data, loading, error];
};
